import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { menuApi } from "@/lib/menuApi";
import { toast } from "sonner";
import { ArrowLeft, Download, Search, Trash2, Eye, EyeOff, X, Plus, Loader2, Image as ImageIcon, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { APOLLO_LOGO } from "@/lib/brand";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

/**
 * Menu Designer Workspace — STRUCTURED MODE.
 *
 * The template defines a fixed layout: header (Apollo wordmark), N product
 * slots at pre-positioned coordinates, and a footer. Retailers can only fill
 * slots — no drag, no resize, no z-order. Each fill is an element:
 *   { slot_id, product_id, render_url, price, promo_price, show, strain, phenotype }
 * A slot is either empty (shows a click-to-add placeholder) or filled
 * (shows the product card composited on-brand).
 */

const APOLLO_TEXT_COLORS = ["#FFFFFF", "#FBC90C", "#E15C2C", "#B02065"];

export default function MenuDesignerWorkspace() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const previewOnly = params.get("preview") === "1";

  const [menu, setMenu] = useState(null);
  const [template, setTemplate] = useState(null);
  const [products, setProducts] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(null); // slot id being filled
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [zoom, setZoom] = useState(0.6);
  const [preview, setPreview] = useState(previewOnly);
  const [exporting, setExporting] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [savingState, setSavingState] = useState("idle");
  const canvasRef = useRef(null);
  const saveTimer = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const m = await menuApi.getMenu(id);
        setMenu(m);
        const t = await menuApi.getTemplate(m.template_id);
        setTemplate(t);
        const p = await api.get("/products").then((r) => r.data);
        setProducts(p);
        // Auto-fit zoom to the viewport so the whole menu is visible on load.
        const availW = window.innerWidth - 700; // account for both side panels
        const availH = window.innerHeight - 140;
        const z = Math.min(availW / t.canvas_width, availH / t.canvas_height, 1);
        setZoom(Math.max(0.25, +z.toFixed(2)));
      } catch {
        toast.error("Could not load menu");
        nav("/menu-designer");
      }
    })();
  }, [id, nav]);

  // Autosave — 900ms debounce after every element change.
  const scheduleSave = useCallback((next) => {
    setSavingState("saving");
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await menuApi.updateMenu(id, { name: next.name, elements: next.elements });
        setSavedAt(new Date());
        setSavingState("saved");
      } catch {
        setSavingState("idle");
        toast.error("Autosave failed");
      }
    }, 900);
  }, [id]);

  useEffect(() => () => saveTimer.current && clearTimeout(saveTimer.current), []);

  function patchMenu(patch) {
    setMenu((prev) => {
      const next = { ...prev, ...patch };
      scheduleSave(next);
      return next;
    });
  }

  function upsertFill(slot_id, patch) {
    setMenu((prev) => {
      const els = prev.elements || [];
      const idx = els.findIndex((e) => e.slot_id === slot_id);
      const nextEl = idx >= 0 ? { ...els[idx], ...patch } : { slot_id, show: { image: true, name: true, strain: true, phenotype: true, price: true }, ...patch };
      const nextEls = idx >= 0 ? els.map((e, i) => (i === idx ? nextEl : e)) : [...els, nextEl];
      const next = { ...prev, elements: nextEls };
      scheduleSave(next);
      return next;
    });
  }

  function clearFill(slot_id) {
    setMenu((prev) => {
      const nextEls = (prev.elements || []).filter((e) => e.slot_id !== slot_id);
      const next = { ...prev, elements: nextEls };
      scheduleSave(next);
      return next;
    });
    if (selectedSlot === slot_id) setSelectedSlot(null);
  }

  const layout = template?.layout || {};
  const slots = layout.slots || [];
  const fillsBySlot = useMemo(() => {
    const map = {};
    (menu?.elements || []).forEach((e) => { map[e.slot_id] = e; });
    return map;
  }, [menu?.elements]);

  const filledCount = Object.keys(fillsBySlot).length;

  // Assign a product to a slot
  function assignProduct(slot, product, variant) {
    const strainName = variant?.name || product.strain;
    const phenotype = (variant?.phenotype || variant?.type || product.phenotype || product.strain || "").toLowerCase();
    const renderUrl = product.hero_image || product.gallery?.[0] || "";
    upsertFill(slot.id, {
      product_id: product.id,
      product_slug: product.slug,
      product_name: product.name,
      product_category: product.category,
      strain: strainName,
      phenotype,
      render_url: renderUrl,
      available_renders: [product.hero_image, ...(product.gallery || [])].filter(Boolean),
      price: "",
      promo_price: "",
    });
    setPickerOpen(null);
    setSelectedSlot(slot.id);
  }

  // Export via html2canvas + jsPDF
  async function exportMenu(format) {
    if (!canvasRef.current) return;
    setExporting(true);
    const pending = toast.loading(`Exporting ${format.toUpperCase()}…`);
    try {
      const scale = 2;
      const canvas = await html2canvas(canvasRef.current, {
        backgroundColor: template.background_color || "#000000",
        useCORS: true,
        allowTaint: true,
        scale,
        logging: false,
      });
      const safeName = (menu.name || "menu").replace(/[^A-Za-z0-9._-]+/g, "_");
      if (format === "png") {
        canvas.toBlob((blob) => triggerDownload(blob, `${safeName}.png`), "image/png");
      } else if (format === "jpg") {
        canvas.toBlob((blob) => triggerDownload(blob, `${safeName}.jpg`), "image/jpeg", 0.95);
      } else if (format === "pdf") {
        const imgData = canvas.toDataURL("image/jpeg", 0.95);
        const orientation = template.canvas_width >= template.canvas_height ? "landscape" : "portrait";
        const pdf = new jsPDF({ orientation, unit: "pt", format: [template.canvas_width, template.canvas_height] });
        pdf.addImage(imgData, "JPEG", 0, 0, template.canvas_width, template.canvas_height);
        pdf.save(`${safeName}.pdf`);
      }
      // Capture a thumbnail so the landing grid stays fresh.
      const thumbCanvas = document.createElement("canvas");
      const maxW = 400;
      thumbCanvas.width = maxW;
      thumbCanvas.height = Math.round((maxW / canvas.width) * canvas.height);
      thumbCanvas.getContext("2d").drawImage(canvas, 0, 0, thumbCanvas.width, thumbCanvas.height);
      const thumb = thumbCanvas.toDataURL("image/jpeg", 0.7);
      menuApi.updateMenu(id, { thumbnail_url: thumb }).catch(() => {});
      menuApi.trackExport(id, format).catch(() => {});
      toast.success(`Exported ${safeName}.${format}`, { id: pending });
    } catch (e) {
      console.error(e);
      toast.error("Export failed", { id: pending });
    } finally {
      setExporting(false);
    }
  }

  function triggerDownload(blob, filename) {
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objUrl), 1500);
  }

  if (!menu || !template) {
    return <div className="text-sm text-muted-foreground flex items-center gap-2 p-10"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading workspace…</div>;
  }

  const savingLabel = savingState === "saving" ? "Saving…" : savingState === "saved" ? (savedAt ? `Saved ${savedAt.toLocaleTimeString()}` : "Saved") : "";
  const selectedFill = selectedSlot ? fillsBySlot[selectedSlot] : null;

  return (
    <div className="fixed inset-0 z-30 bg-background flex flex-col" data-testid="menu-workspace">
      {/* Top bar */}
      <div className="border-b border-border bg-surface flex items-center gap-3 px-4 py-2">
        <button onClick={() => nav("/menu-designer")} className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground transition" data-testid="workspace-back">
          <ArrowLeft className="h-3 w-3" /> Menus
        </button>
        <input
          value={menu.name}
          onChange={(e) => patchMenu({ name: e.target.value })}
          className="bg-transparent border border-transparent hover:border-border rounded-sm px-2 py-1 text-sm font-semibold min-w-[220px]"
          data-testid="workspace-name"
        />
        <span className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground truncate">{template.name}</span>
        <span className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground ml-auto">
          {filledCount}/{slots.length} slots · {savingLabel}
        </span>
        <div className="flex items-center gap-1">
          <button onClick={() => setZoom((z) => Math.max(0.2, +(z - 0.1).toFixed(2)))} className="p-1.5 border border-border rounded-sm hover:bg-surface-elev" title="Zoom out" data-testid="zoom-out">−</button>
          <span className="text-xs font-mono w-10 text-center">{Math.round(zoom * 100)}%</span>
          <button onClick={() => setZoom((z) => Math.min(2, +(z + 0.1).toFixed(2)))} className="p-1.5 border border-border rounded-sm hover:bg-surface-elev" title="Zoom in" data-testid="zoom-in">+</button>
        </div>
        <button
          onClick={() => setPreview((p) => !p)}
          className="ml-2 px-3 py-1.5 border border-border rounded-sm text-xs font-mono uppercase tracking-widest hover:bg-surface-elev flex items-center gap-1.5"
          data-testid="toggle-preview"
        >
          {preview ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
          {preview ? "Edit" : "Preview"}
        </button>
        <ExportMenu onExport={exportMenu} disabled={exporting} />
      </div>

      {/* Body */}
      <div className="flex-1 flex overflow-hidden">
        {/* Center — Canvas */}
        <div className="flex-1 overflow-auto bg-muted/40 flex items-start justify-center p-8" onClick={() => setSelectedSlot(null)}>
          <div style={{ width: template.canvas_width * zoom, height: template.canvas_height * zoom }}>
            <div
              ref={canvasRef}
              className="relative shadow-2xl origin-top-left"
              style={{
                width: template.canvas_width,
                height: template.canvas_height,
                transform: `scale(${zoom})`,
                transformOrigin: "top left",
                backgroundColor: template.background_color || "#000",
                backgroundImage: template.background_url
                  ? `url(${template.background_url.startsWith("/") ? process.env.REACT_APP_BACKEND_URL + template.background_url : template.background_url})`
                  : undefined,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
              data-testid="menu-canvas"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header — Apollo wordmark. Suppressed when the template ships
                  with a background image (those already carry Apollo art). */}
              {layout.header && !template.background_url && (
                <div
                  className="absolute flex flex-col items-center justify-center"
                  style={{ left: layout.header.x, top: layout.header.y, width: layout.header.w, height: layout.header.h }}
                >
                  <img
                    src={APOLLO_LOGO}
                    alt="APOLLO"
                    crossOrigin="anonymous"
                    className="object-contain"
                    style={{ height: layout.header.h * 0.62, maxWidth: layout.header.w * 0.6 }}
                    draggable={false}
                  />
                  <div
                    className="mt-2 font-mono uppercase tracking-[0.5em] opacity-70"
                    style={{ color: template.accent_color || "#FBC90C", fontSize: Math.max(10, layout.header.h * 0.14) }}
                  >
                    {layout.header.subtitle || "MENU"}
                  </div>
                  {/* Accent underline */}
                  <div className="absolute left-0 right-0 mx-auto" style={{ bottom: 0, height: 2, width: "40%", background: template.accent_color || "#FBC90C" }} />
                </div>
              )}

              {/* Slots */}
              {slots.map((slot) => {
                const fill = fillsBySlot[slot.id];
                const isSelected = !preview && selectedSlot === slot.id;
                return (
                  <SlotNode
                    key={slot.id}
                    slot={slot}
                    fill={fill}
                    selected={isSelected}
                    preview={preview}
                    accent={template.accent_color}
                    onClick={() => {
                      if (preview) return;
                      if (fill) setSelectedSlot(slot.id);
                      else setPickerOpen(slot.id);
                    }}
                  />
                );
              })}

              {/* Footer — suppressed for custom backgrounds. */}
              {layout.footer && !template.background_url && (
                <div
                  className="absolute flex items-center justify-center border-t"
                  style={{
                    left: layout.footer.x,
                    top: layout.footer.y,
                    width: layout.footer.w,
                    height: layout.footer.h,
                    borderColor: `${template.accent_color || "#FBC90C"}33`,
                  }}
                >
                  <div
                    className="font-mono uppercase whitespace-nowrap"
                    style={{
                      color: "#ffffff99",
                      fontSize: Math.min(layout.footer.h * 0.32, layout.footer.w * 0.022),
                      letterSpacing: "0.28em",
                    }}
                  >
                    {layout.footer.text || "ELEVATE BEYOND THE ORDINARY"}
                  </div>
                </div>
              )}

              {/* Brand marks — decorative Apollo marks positioned in corners */}
              {(menu.brand_marks || []).map((bm) => (
                <BrandMarkOnCanvas key={`${bm.id}-${bm.corner}`} mark={bm} canvasW={template.canvas_width} canvasH={template.canvas_height} />
              ))}
            </div>
          </div>
        </div>

        {/* Right — Slot Settings */}
        {!preview && (
          <aside className="w-80 border-l border-border bg-surface overflow-y-auto" data-testid="slot-settings">
            {selectedFill ? (
              <SlotSettings
                fill={selectedFill}
                onChange={(patch) => upsertFill(selectedFill.slot_id, patch)}
                onClear={() => clearFill(selectedFill.slot_id)}
                onSwap={() => setPickerOpen(selectedFill.slot_id)}
              />
            ) : (
              <div className="p-6 text-sm text-muted-foreground">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] mb-2">Structured Menu</div>
                <p className="mb-3">Click any empty slot to add a product. Filled slots let you swap the render, edit pricing, and toggle which fields are shown.</p>
                <p>Layout and Apollo branding stay locked so every menu you export stays on-brand.</p>
              </div>
            )}
          </aside>
        )}
      </div>

      {/* Product Picker Modal */}
      {pickerOpen && (
        <ProductPicker
          products={products}
          onPick={(product, variant) => {
            const slot = slots.find((s) => s.id === pickerOpen);
            if (slot) assignProduct(slot, product, variant);
          }}
          onClose={() => setPickerOpen(null)}
        />
      )}
    </div>
  );
}

// ---------- Slot rendering ----------

function SlotNode({ slot, fill, selected, preview, accent, onClick }) {
  const style = { left: slot.x, top: slot.y, width: slot.w, height: slot.h };
  const outline = selected ? { outline: `2px solid ${accent || "#FBC90C"}`, outlineOffset: 2 } : {};
  if (!fill) {
    if (preview) return null; // hidden in preview mode
    return (
      <button
        className="absolute rounded-sm flex flex-col items-center justify-center gap-2 border-2 border-dashed transition hover:bg-white/5"
        style={{ ...style, borderColor: `${accent || "#FBC90C"}55`, background: "rgba(255,255,255,0.02)" }}
        onClick={(e) => { e.stopPropagation(); onClick(); }}
        data-testid={`slot-empty-${slot.id}`}
      >
        <Plus className="h-6 w-6" style={{ color: accent || "#FBC90C" }} />
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/70">{slot.kind === "product_hero" ? "Featured Product" : "Add Product"}</span>
      </button>
    );
  }
  const isRow = slot.kind === "product_row";
  const isHero = slot.kind === "product_hero";
  const isSplit = slot.kind === "product_split";
  return (
    <div
      className={`absolute ${preview ? "" : "cursor-pointer"} rounded-sm`}
      style={{ ...style, ...outline }}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      data-testid={`slot-filled-${slot.id}`}
    >
      {isRow ? <ProductRowFill fill={fill} accent={accent} /> :
       isHero ? <ProductHeroFill fill={fill} accent={accent} /> :
       isSplit ? <ProductSplitFill fill={fill} accent={accent} /> :
       <ProductCardFill fill={fill} accent={accent} />}
    </div>
  );
}

function ProductCardFill({ fill, accent }) {
  return (
    <div className="w-full h-full flex flex-col items-center text-center px-2 py-3" style={{ color: fill.text_color || "#FFFFFF", fontFamily: "'Poppins', system-ui, sans-serif" }}>
      {fill.show?.image && fill.render_url && (
        <div className="w-full flex-1 flex items-center justify-center">
          <img
            src={fill.render_url}
            alt={fill.product_name}
            crossOrigin="anonymous"
            className="max-h-full max-w-full object-contain"
            style={{ maxHeight: "70%" }}
            draggable={false}
          />
        </div>
      )}
      <div className="w-full">
        {fill.show?.name && (
          <div className="font-bold uppercase tracking-wide leading-tight" style={{ fontSize: "clamp(11px, 2.2vw, 20px)" }}>
            {fill.product_name}
          </div>
        )}
        <div className="flex items-baseline justify-center gap-1.5 mt-1 opacity-90" style={{ fontSize: "clamp(9px, 1.4vw, 13px)" }}>
          {fill.show?.strain && <span>{fill.strain}</span>}
          {fill.show?.strain && fill.show?.phenotype && <span className="opacity-40">·</span>}
          {fill.show?.phenotype && <span className="uppercase tracking-widest">{fill.phenotype}</span>}
        </div>
        {fill.show?.price && (fill.price || fill.promo_price) && (
          <div className="mt-1.5 flex items-baseline justify-center gap-2" style={{ fontSize: "clamp(13px, 2.4vw, 22px)" }}>
            {fill.promo_price ? (
              <>
                <span className="font-bold" style={{ color: accent || "#FBC90C" }}>${fill.promo_price}</span>
                {fill.price && <span className="line-through opacity-50 text-xs">${fill.price}</span>}
              </>
            ) : (
              <span className="font-bold" style={{ color: accent || "#FBC90C" }}>${fill.price}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ProductHeroFill({ fill, accent }) {
  return (
    <div className="w-full h-full flex items-center gap-6 px-6 py-4" style={{ color: fill.text_color || "#FFFFFF", fontFamily: "'Poppins', system-ui, sans-serif" }}>
      {fill.show?.image && fill.render_url && (
        <div className="h-full flex-shrink-0 flex items-center justify-center" style={{ width: "48%" }}>
          <img src={fill.render_url} alt={fill.product_name} crossOrigin="anonymous" className="max-h-full max-w-full object-contain" draggable={false} />
        </div>
      )}
      <div className="flex-1">
        {fill.show?.phenotype && (
          <div className="font-mono uppercase tracking-[0.4em] text-xs opacity-70" style={{ color: accent || "#FBC90C" }}>{fill.phenotype}</div>
        )}
        {fill.show?.name && (
          <div className="font-bold uppercase leading-tight mt-2" style={{ fontSize: "clamp(24px, 4vw, 48px)" }}>
            {fill.product_name}
          </div>
        )}
        {fill.show?.strain && (
          <div className="mt-2 opacity-90" style={{ fontSize: "clamp(14px, 1.8vw, 22px)" }}>{fill.strain}</div>
        )}
        {fill.show?.price && (fill.price || fill.promo_price) && (
          <div className="mt-4 flex items-baseline gap-3" style={{ fontSize: "clamp(20px, 3.2vw, 44px)" }}>
            {fill.promo_price ? (
              <>
                <span className="font-bold" style={{ color: accent || "#FBC90C" }}>${fill.promo_price}</span>
                {fill.price && <span className="line-through opacity-50 text-lg">${fill.price}</span>}
              </>
            ) : (
              <span className="font-bold" style={{ color: accent || "#FBC90C" }}>${fill.price}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ProductRowFill({ fill, accent }) {
  return (
    <div className="w-full h-full flex items-center gap-4 px-3 py-2 border-b" style={{ color: fill.text_color || "#FFFFFF", borderColor: `${accent || "#FBC90C"}22`, fontFamily: "'Poppins', system-ui, sans-serif" }}>
      {fill.show?.image && fill.render_url && (
        <div className="h-full aspect-square flex items-center justify-center flex-shrink-0">
          <img src={fill.render_url} alt={fill.product_name} crossOrigin="anonymous" className="max-h-full max-w-full object-contain" draggable={false} />
        </div>
      )}
      <div className="flex-1 min-w-0">
        {fill.show?.name && (
          <div className="font-bold uppercase truncate" style={{ fontSize: "clamp(14px, 1.6vw, 22px)" }}>{fill.product_name}</div>
        )}
        <div className="flex items-baseline gap-2 opacity-90" style={{ fontSize: "clamp(10px, 1.1vw, 14px)" }}>
          {fill.show?.strain && <span>{fill.strain}</span>}
          {fill.show?.strain && fill.show?.phenotype && <span className="opacity-40">·</span>}
          {fill.show?.phenotype && <span className="uppercase tracking-widest">{fill.phenotype}</span>}
        </div>
      </div>
      {fill.show?.price && (fill.price || fill.promo_price) && (
        <div className="flex items-baseline gap-2 flex-shrink-0" style={{ fontSize: "clamp(16px, 2vw, 26px)" }}>
          {fill.promo_price ? (
            <>
              <span className="font-bold" style={{ color: accent || "#FBC90C" }}>${fill.promo_price}</span>
              {fill.price && <span className="line-through opacity-50 text-sm">${fill.price}</span>}
            </>
          ) : (
            <span className="font-bold" style={{ color: accent || "#FBC90C" }}>${fill.price}</span>
          )}
        </div>
      )}
    </div>
  );
}

// Split layout: image LEFT · name/strain center · big price RIGHT.
// The dedicated split kind gives more breathing room than a compact row and
// treats price as the visual hero, which is what retailers usually want.
function ProductSplitFill({ fill, accent }) {
  return (
    <div className="w-full h-full flex items-center gap-3 px-4 py-2" style={{ color: fill.text_color || "#FFFFFF", fontFamily: "'Poppins', system-ui, sans-serif" }}>
      {fill.show?.image && fill.render_url && (
        <div className="h-full flex items-center justify-center flex-shrink-0" style={{ width: "35%" }}>
          <img src={fill.render_url} alt={fill.product_name} crossOrigin="anonymous" className="max-h-full max-w-full object-contain" draggable={false} />
        </div>
      )}
      <div className="flex-1 min-w-0">
        {fill.show?.name && (
          <div className="font-bold uppercase leading-tight" style={{ fontSize: "clamp(14px, 2.2vw, 24px)" }}>{fill.product_name}</div>
        )}
        <div className="flex items-baseline gap-2 mt-1 opacity-90" style={{ fontSize: "clamp(10px, 1.4vw, 15px)" }}>
          {fill.show?.strain && <span>{fill.strain}</span>}
          {fill.show?.strain && fill.show?.phenotype && <span className="opacity-40">·</span>}
          {fill.show?.phenotype && <span className="uppercase tracking-widest">{fill.phenotype}</span>}
        </div>
      </div>
      {fill.show?.price && (fill.price || fill.promo_price) && (
        <div className="flex flex-col items-end flex-shrink-0 pl-3 border-l" style={{ borderColor: `${accent || "#FBC90C"}44`, minWidth: 90 }}>
          {fill.promo_price ? (
            <>
              <span className="font-bold" style={{ color: accent || "#FBC90C", fontSize: "clamp(18px, 3vw, 34px)" }}>${fill.promo_price}</span>
              {fill.price && <span className="line-through opacity-50 text-xs mt-0.5">${fill.price}</span>}
            </>
          ) : (
            <span className="font-bold" style={{ color: accent || "#FBC90C", fontSize: "clamp(18px, 3vw, 34px)" }}>${fill.price}</span>
          )}
        </div>
      )}
    </div>
  );
}

// A single decorative brand mark pinned to one of the four canvas corners.
// Kept lightweight — the mark is a plain image at 8% of canvas width with
// margins so it never overlaps template header/footer content.
function BrandMarkOnCanvas({ mark, canvasW, canvasH }) {
  const size = mark.size || Math.round(canvasW * 0.09);
  const inset = Math.round(canvasW * 0.02);
  const pos = { tl: { left: inset, top: inset }, tr: { right: inset, top: inset }, bl: { left: inset, bottom: inset }, br: { right: inset, bottom: inset } }[mark.corner || "tl"];
  return (
    <img
      src={mark.url}
      alt="Apollo brand mark"
      crossOrigin="anonymous"
      className="absolute object-contain pointer-events-none"
      style={{ ...pos, width: size, height: size, opacity: mark.opacity ?? 1 }}
      draggable={false}
      data-testid={`brand-mark-${mark.id}-${mark.corner}`}
    />
  );
}


// ---------- Right panel: Brand Marks ----------

// Curated set of Apollo brand marks retailers can drop onto a menu. Sourced
// from the shared brand asset URLs so the entire portal stays in lockstep.
const BRAND_MARKS = [
  { id: "apollo-primary", label: "Primary Brand Mark", url: APOLLO_LOGO },
  { id: "apollo-secondary", label: "Secondary Brand Mark", url: "https://customer-assets-7cd3h4nn.emergentagent.net/job_brand-command-22/artifacts/2ojdu1o7_Untitled%20design%20-%202026-08-06T223418.025.png" },
];
const CORNERS = [
  { id: "tl", label: "Top-Left" },
  { id: "tr", label: "Top-Right" },
  { id: "bl", label: "Bottom-Left" },
  { id: "br", label: "Bottom-Right" },
];

function BrandMarksPanel({ marks, onChange }) {
  function isActive(markId, corner) {
    return marks.some((m) => m.id === markId && m.corner === corner);
  }
  function toggle(markId, corner) {
    const url = BRAND_MARKS.find((b) => b.id === markId)?.url;
    if (!url) return;
    if (isActive(markId, corner)) {
      onChange(marks.filter((m) => !(m.id === markId && m.corner === corner)));
    } else {
      onChange([...marks, { id: markId, corner, url }]);
    }
  }
  return (
    <div className="p-4 space-y-6 text-sm" data-testid="brand-marks-panel">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Structured Menu</div>
        <p className="mt-2 text-muted-foreground">Click any empty slot to add a product. Layout and Apollo branding stay locked so every export is on-brand.</p>
      </div>

      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-3">Brand Marks</div>
        <p className="text-xs text-muted-foreground mb-3">Add approved Apollo marks to any corner of the menu.</p>
        <div className="space-y-4">
          {BRAND_MARKS.map((bm) => (
            <div key={bm.id} className="border border-border rounded-sm p-3">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-sm bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
                  <img src={bm.url} alt={bm.label} className="max-w-full max-h-full object-contain" />
                </div>
                <div className="text-xs font-semibold">{bm.label}</div>
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                {CORNERS.map((c) => {
                  const active = isActive(bm.id, c.id);
                  return (
                    <button
                      key={c.id}
                      onClick={() => toggle(bm.id, c.id)}
                      className={`text-[10px] uppercase tracking-widest border rounded-sm py-1.5 transition ${active ? "border-foreground bg-foreground text-background" : "border-border text-muted-foreground hover:text-foreground"}`}
                      data-testid={`brandmark-${bm.id}-${c.id}`}
                    >
                      {c.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------- Right panel: Slot Settings ----------

function SlotSettings({ fill, onChange, onClear, onSwap }) {
  const toggle = (key) => onChange({ show: { ...fill.show, [key]: !fill.show?.[key] } });
  const renders = (fill.available_renders && fill.available_renders.length > 0) ? fill.available_renders : (fill.render_url ? [fill.render_url] : []);
  return (
    <div className="p-4 space-y-5 text-sm">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Slot · {fill.slot_id.toUpperCase()}</div>
        <div className="font-display text-lg font-bold mt-1">{fill.product_name}</div>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">{fill.strain} · {fill.phenotype}</div>
      </div>

      {renders.length > 1 && (
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">Render</div>
          <div className="grid grid-cols-3 gap-1.5">
            {renders.map((r) => (
              <button
                key={r}
                onClick={() => onChange({ render_url: r })}
                className={`aspect-square rounded-sm border overflow-hidden ${fill.render_url === r ? "border-foreground" : "border-border"}`}
                data-testid={`render-swatch-${r.slice(-10)}`}
              >
                <img src={r} alt="" className="w-full h-full object-contain bg-muted" />
              </button>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">Show / Hide</div>
        <div className="grid grid-cols-2 gap-1.5">
          {[
            ["image", "Image"],
            ["name", "Name"],
            ["strain", "Strain"],
            ["phenotype", "Phenotype"],
            ["price", "Price"],
          ].map(([key, label]) => (
            <button
              key={key}
              onClick={() => toggle(key)}
              className={`text-xs uppercase tracking-widest border rounded-sm py-1.5 ${fill.show?.[key] ? "border-foreground bg-foreground text-background" : "border-border text-muted-foreground"}`}
              data-testid={`toggle-${key}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">Pricing</div>
        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs">
            <span className="text-muted-foreground">Retail</span>
            <input value={fill.price || ""} onChange={(e) => onChange({ price: e.target.value })} placeholder="e.g. 40" className="w-full bg-transparent border border-border rounded-sm px-2 py-1.5 mt-1" data-testid="price-input" />
          </label>
          <label className="text-xs">
            <span className="text-muted-foreground">Promo</span>
            <input value={fill.promo_price || ""} onChange={(e) => onChange({ promo_price: e.target.value })} placeholder="optional" className="w-full bg-transparent border border-border rounded-sm px-2 py-1.5 mt-1" data-testid="promo-price-input" />
          </label>
        </div>
      </div>

      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">Text Color</div>
        <div className="flex gap-1.5">
          {APOLLO_TEXT_COLORS.map((c) => (
            <button
              key={c}
              onClick={() => onChange({ text_color: c })}
              className="w-7 h-7 rounded-full border"
              style={{ background: c, borderColor: fill.text_color === c ? "#FBC90C" : "rgba(255,255,255,0.2)", borderWidth: fill.text_color === c ? 2 : 1 }}
              title={c}
              data-testid={`color-${c}`}
            />
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <button onClick={onSwap} className="border border-border rounded-sm py-2 text-xs font-mono uppercase tracking-widest hover:bg-surface-elev flex items-center justify-center gap-1.5" data-testid="slot-swap">
          <ImageIcon className="h-3 w-3" /> Swap product
        </button>
        <button onClick={onClear} className="rounded-sm py-2 text-xs font-mono uppercase tracking-widest text-white flex items-center justify-center gap-1.5" style={{ background: "#E15C2C" }} data-testid="slot-clear">
          <Trash2 className="h-3 w-3" /> Clear slot
        </button>
      </div>
    </div>
  );
}

// ---------- Product Picker Modal ----------

// Small pill-shaped filter chip used by the Product Picker. Simple button
// styled to feel like a segmented control so the "Vape / Pre Rolls" and
// strain toggles read as one clean row instead of stacked dropdowns.
function FilterPill({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      className={`px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-widest border transition ${active ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground hover:text-foreground"}`}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

function ProductPicker({ products, onPick, onClose }) {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [strain, setStrain] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  // Category options built from the real product master data. We display a
  // prettified label ("Pre Rolls") but filter on the raw value ("pre-rolls").
  const categories = useMemo(() => {
    const set = new Set();
    products.forEach((p) => { if (p.category) set.add(p.category); });
    return Array.from(set);
  }, [products]);
  const prettyCategory = (c) => (c || "")
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
  const filtered = useMemo(() => {
    let out = products;
    if (category) out = out.filter((p) => p.category === category);
    if (strain) out = out.filter((p) => p.strain === strain || (p.strains || []).some((s) => (s.type || "").toLowerCase() === strain));
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      out = out.filter((p) => (p.name || "").toLowerCase().includes(q));
    }
    return out;
  }, [products, category, strain, search]);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-surface border border-border rounded-sm w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="product-picker">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Choose a product</div>
            <div className="font-display text-lg font-bold">Product Library</div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-surface-elev rounded-sm"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-3 border-b border-border space-y-2">
          {/* Row 1 — search */}
          <div className="relative">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products…" className="w-full bg-transparent border border-border rounded-sm pl-8 pr-2 py-1.5 text-sm" data-testid="picker-search" />
          </div>
          {/* Row 2 — filters as pill groups so the "Vape / Pre Rolls" and strain choices are one tap each */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <div className="flex items-center gap-1.5">
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mr-1">Product Type</span>
              <FilterPill active={category === ""} onClick={() => setCategory("")} testId="picker-cat-all">All</FilterPill>
              {categories.map((c) => (
                <FilterPill key={c} active={category === c} onClick={() => setCategory(c)} testId={`picker-cat-${c}`}>{prettyCategory(c)}</FilterPill>
              ))}
            </div>
            <div className="flex items-center gap-1.5">
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mr-1">Strain</span>
              <FilterPill active={strain === ""} onClick={() => setStrain("")} testId="picker-strain-all">All</FilterPill>
              <FilterPill active={strain === "indica"} onClick={() => setStrain("indica")} testId="picker-strain-indica">Indica</FilterPill>
              <FilterPill active={strain === "sativa"} onClick={() => setStrain("sativa")} testId="picker-strain-sativa">Sativa</FilterPill>
              <FilterPill active={strain === "hybrid"} onClick={() => setStrain("hybrid")} testId="picker-strain-hybrid">Hybrid</FilterPill>
            </div>
            {(category || strain) && (
              <button
                onClick={() => { setCategory(""); setStrain(""); }}
                className="ml-auto text-[10px] font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground transition"
                data-testid="picker-clear-filters"
              >
                Clear
              </button>
            )}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {filtered.length === 0 && <div className="text-xs text-muted-foreground p-6 text-center">No products match.</div>}
          {filtered.map((p) => (
            <PickerRow key={p.id} product={p} expanded={expandedId === p.id} onExpand={() => setExpandedId(expandedId === p.id ? null : p.id)} onPick={(v) => onPick(p, v)} />
          ))}
        </div>
      </div>
    </div>
  );
}

function PickerRow({ product, expanded, onExpand, onPick }) {
  const variants = product.strains && product.strains.length > 0 ? product.strains : null;
  return (
    <div className="border border-border rounded-sm bg-background/50">
      <div className="p-2 flex items-center gap-3">
        <div className="w-14 h-14 rounded-sm bg-muted overflow-hidden flex-shrink-0">
          {product.hero_image && <img src={product.hero_image} alt={product.name} className="w-full h-full object-contain" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold truncate">{product.name}</div>
          <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground truncate">{product.category} · {product.strain}</div>
        </div>
        {variants ? (
          <button onClick={onExpand} className="p-1.5 rounded-sm border border-border hover:bg-surface-elev flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest" data-testid={`picker-expand-${product.slug}`}>
            {variants.length} strains <ChevronDown className={`h-3 w-3 transition ${expanded ? "rotate-180" : ""}`} />
          </button>
        ) : (
          <button onClick={() => onPick()} className="px-3 py-1.5 rounded-sm bg-foreground text-background text-[10px] font-mono uppercase tracking-widest hover:opacity-90" data-testid={`picker-pick-${product.slug}`}>
            Pick
          </button>
        )}
      </div>
      {expanded && variants && (
        <div className="border-t border-border p-2 space-y-1">
          {variants.map((v) => (
            <button
              key={v.name}
              onClick={() => onPick(v)}
              className="w-full flex items-center justify-between text-left px-2 py-2 rounded-sm hover:bg-surface-elev text-sm"
              data-testid={`picker-variant-${product.slug}-${v.name}`}
            >
              <span>{v.name}</span>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{v.type}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Export dropdown ----------

function ExportMenu({ onExport, disabled }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button onClick={() => setOpen((o) => !o)} disabled={disabled} className="ml-2 px-3 py-1.5 uppercase text-xs font-semibold tracking-widest flex items-center gap-1.5 disabled:opacity-50" style={{ background: "#FBC90C", color: "#000" }} data-testid="export-btn">
        <Download className="h-3.5 w-3.5" /> Export
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 border border-border bg-surface rounded-sm shadow-xl z-10 min-w-[160px]" onMouseLeave={() => setOpen(false)}>
          {["pdf", "png", "jpg"].map((f) => (
            <button key={f} onClick={() => { setOpen(false); onExport(f); }} className="w-full text-left px-3 py-2 hover:bg-surface-elev text-xs font-mono uppercase tracking-widest" data-testid={`export-${f}`}>
              {f.toUpperCase()}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
